import React, {
  createContext,
  useState,
  useEffect,
  useRef,
  useContext,
} from 'react';
import axios, {
  setActivityResetter,
  setSessionLogoutOnce,
} from '../api/axiosClient';
import i18n from '../i18n';
import { setStoredLanguage, getStoredLanguage } from '../utils/languageService';
import { readStoredAuthUser, normalizeTenantUser } from '../utils/adminShopContext';
import { clearAiStudioSessions } from '../utils/haircutsStudioSession';
import SessionTimeoutModal from '../components/auth/SessionTimeoutModal';

export const AuthContext = createContext();

// ---------------------------------------------------------------------------
// Session-timeout constants
// ---------------------------------------------------------------------------
const IDLE_TIMEOUT_MS      = 60 * 60 * 1000;  // 60 min total inactivity -> logout
const WARNING_LEAD_MS      = 5 * 60 * 1000;   // show warning 5 min before logout
const WARNING_INTERVAL_MS  = 1_000;           // countdown tick (1 s)
const ACTIVITY_THROTTLE_MS = 10_000;          // minimum gap between resetActivity calls

// Cross-tab sync
const CROSS_TAB_CHANNEL = 'hairchitech_session';
const STORAGE_SYNC_KEY  = 'hairchitech_session_event';

// ---------------------------------------------------------------------------
// localStorage helpers (unchanged from original)
// ---------------------------------------------------------------------------
const readStoredUser  = () => readStoredAuthUser();

const writeStoredUser = (user) => {
  if (!user) { localStorage.removeItem('user'); return; }
  localStorage.setItem('user', JSON.stringify(user));
};

const applyUserLanguage = (user) => {
  if (user?.language && ['en', 'fr'].includes(user.language)) {
    const stored = getStoredLanguage();
    if (!stored) {
      i18n.changeLanguage(user.language);
      setStoredLanguage(user.language);
    }
  }
};

// ---------------------------------------------------------------------------
// Decode JWT `iat` from localStorage — no network call required.
// Returns the claim as Unix seconds, or null on any failure.
// ---------------------------------------------------------------------------
const decodeJwtIat = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.iat === 'number' ? payload.iat : null;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// AuthProvider
// ---------------------------------------------------------------------------
export const AuthProvider = ({ children }) => {
  const [user, setUser]                             = useState(() => readStoredUser());
  const [loading, setLoading]                       = useState(true);
  const [warningVisible, setWarningVisible]         = useState(false);
  const [warningSecondsLeft, setWarningSecondsLeft] = useState(
    Math.round(WARNING_LEAD_MS / 1000)
  );

  // ------------------------------------------------------------------
  // Refs — survive re-renders without triggering effects
  // ------------------------------------------------------------------
  const isLoggingOutRef    = useRef(false);  // deduplication guard
  const idleTimerRef       = useRef(null);   // setTimeout for idle expiry
  const warningIntervalRef = useRef(null);   // setInterval for countdown
  const absoluteTimerRef   = useRef(null);   // setTimeout for absolute expiry
  const lastActivityRef    = useRef(0);      // throttle gate
  const channelRef         = useRef(null);   // BroadcastChannel

  // Stable ref to current user so timer callbacks always see the live value
  // without having to be recreated on every render.
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  // ------------------------------------------------------------------
  // stopAllTimers
  // ------------------------------------------------------------------
  const stopAllTimers = () => {
    clearTimeout(idleTimerRef.current);
    clearTimeout(absoluteTimerRef.current);
    clearInterval(warningIntervalRef.current);
    idleTimerRef.current      = null;
    absoluteTimerRef.current  = null;
    warningIntervalRef.current = null;
  };

  // ------------------------------------------------------------------
  // broadcastEvent — sends to sibling tabs via BroadcastChannel +
  // localStorage fallback (for browsers without BroadcastChannel).
  // ------------------------------------------------------------------
  const broadcastEvent = (type, payload = {}) => {
    const msg = JSON.stringify({ type, ts: Date.now(), ...payload });
    try { channelRef.current?.postMessage(msg); } catch (_) {}
    // Always write to storage as well so tabs that lost the channel sync too.
    try { localStorage.setItem(STORAGE_SYNC_KEY, msg); } catch (_) {}
  };

  // ------------------------------------------------------------------
  // logoutOnce — deduplicated logout entry point.
  // Called from: idle timer, absolute timer, 401 interceptor, manual logout,
  // and cross-tab SESSION_LOGOUT events.
  // ------------------------------------------------------------------
  const logoutOnce = (reason = 'manual') => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;

    stopAllTimers();
    setWarningVisible(false);

    try {
      if (typeof window !== 'undefined') {
        window.__suppressToasts = true;
        setTimeout(() => { try { window.__suppressToasts = false; } catch (_) {} }, 2500);
      }
    } catch (_) {}

    // Notify other tabs before clearing storage so the event is still visible.
    if (reason !== 'cross-tab') {
      broadcastEvent('SESSION_LOGOUT');
    }

    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem(STORAGE_SYNC_KEY);
    clearAiStudioSessions();
    setUser(null);
  };

  // ------------------------------------------------------------------
  // showWarning — starts the countdown modal.
  // ------------------------------------------------------------------
  const showWarning = () => {
    setWarningVisible(true);
    setWarningSecondsLeft(Math.round(WARNING_LEAD_MS / 1000));

    clearInterval(warningIntervalRef.current);
    warningIntervalRef.current = setInterval(() => {
      setWarningSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(warningIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, WARNING_INTERVAL_MS);
  };

  // ------------------------------------------------------------------
  // scheduleIdleTimer — (re)sets the two-stage idle countdown.
  // Only meaningful while a session is active (callers guard on userRef).
  // ------------------------------------------------------------------
  const scheduleIdleTimer = () => {
    clearTimeout(idleTimerRef.current);
    const warningAt = IDLE_TIMEOUT_MS - WARNING_LEAD_MS;

    idleTimerRef.current = setTimeout(() => {
      if (!userRef.current) return;
      showWarning();

      // Final hard logout after the warning window expires
      idleTimerRef.current = setTimeout(() => {
        if (!userRef.current) return;
        logoutOnce('idle');
      }, WARNING_LEAD_MS);
    }, warningAt);
  };

  // ------------------------------------------------------------------
  // startAbsoluteTimer — one-shot timer based on JWT `iat`.
  // Mirrors the backend SESSION_MAX_AGE_SEC (12 h) as source of truth.
  // ------------------------------------------------------------------
  const startAbsoluteTimer = () => {
    clearTimeout(absoluteTimerRef.current);

    const iat = decodeJwtIat();
    if (!iat) return;

    const ABSOLUTE_MS = 12 * 60 * 60 * 1000;
    const elapsed     = Date.now() - iat * 1000;
    const remaining   = ABSOLUTE_MS - elapsed;

    if (remaining <= 0) {
      logoutOnce('absolute');
      return;
    }

    absoluteTimerRef.current = setTimeout(() => {
      if (!userRef.current) return;
      logoutOnce('absolute');
    }, remaining);
  };

  // ------------------------------------------------------------------
  // resetActivity — USER-ORIGINATED activity ONLY.
  //
  // - Throttled by ACTIVITY_THROTTLE_MS.
  // - Restarts the idle timer.
  // - Hides the warning modal if showing.
  // - Broadcasts SESSION_ACTIVITY to other tabs.
  // - Writes the shared timestamp to localStorage.
  //
  // MUST NOT be called from cross-tab events (use syncActivityFromExternal).
  // ------------------------------------------------------------------
  const resetActivity = () => {
    if (!userRef.current) return;    // no session → do nothing

    const now = Date.now();
    if (now - lastActivityRef.current < ACTIVITY_THROTTLE_MS) return;
    lastActivityRef.current = now;

    scheduleIdleTimer();

    if (warningVisible) {
      setWarningVisible(false);
      clearInterval(warningIntervalRef.current);
    }

    // Tell other tabs this tab just had user activity.
    broadcastEvent('SESSION_ACTIVITY', { lastActivity: now });
  };

  // ------------------------------------------------------------------
  // syncActivityFromExternal — CROSS-TAB activity sync ONLY.
  //
  // - Updates local refs and restarts idle timers.
  // - Hides the warning modal if open.
  // - MUST NOT broadcast.
  // - MUST NOT write localStorage.
  // ------------------------------------------------------------------
  const syncActivityFromExternal = (ts) => {
    if (!userRef.current) return;

    // Only accept timestamps newer than what we already know.
    if (ts <= lastActivityRef.current) return;
    lastActivityRef.current = ts;

    scheduleIdleTimer();

    // Hide modal if another tab proved the user is still active.
    setWarningVisible((prev) => {
      if (prev) clearInterval(warningIntervalRef.current);
      return false;
    });
  };

  // ------------------------------------------------------------------
  // extendSession — user clicked "Stay logged in" in the modal.
  // ------------------------------------------------------------------
  const extendSession = () => {
    setWarningVisible(false);
    clearInterval(warningIntervalRef.current);
    // Bypass throttle by zeroing the gate, then call as local activity.
    lastActivityRef.current = 0;
    resetActivity();
    broadcastEvent('SESSION_EXTENDED');
  };

  // ------------------------------------------------------------------
  // revalidateOnFocus — re-check session validity when tab becomes visible.
  // ------------------------------------------------------------------
  const revalidateOnFocus = () => {
    if (!userRef.current) return;

    // Check absolute session age client-side before waiting for a network call.
    const iat = decodeJwtIat();
    if (iat) {
      const ABSOLUTE_MS = 12 * 60 * 60 * 1000;
      if (Date.now() - iat * 1000 >= ABSOLUTE_MS) {
        logoutOnce('absolute');
        return;
      }
    }

    // Sync the latest shared activity timestamp from storage so this tab
    // resumes the correct idle position even if it was backgrounded.
    try {
      const raw = localStorage.getItem(STORAGE_SYNC_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (
          parsed.type === 'SESSION_ACTIVITY' &&
          typeof parsed.lastActivity === 'number' &&
          parsed.lastActivity > lastActivityRef.current
        ) {
          // Use syncActivityFromExternal — NOT resetActivity — so we do not
          // re-broadcast or write storage.
          syncActivityFromExternal(parsed.lastActivity);
          return;
        }
      }
    } catch (_) {}

    // No newer external activity found; resume idle countdown from now.
    scheduleIdleTimer();
  };

  // ------------------------------------------------------------------
  // handleCrossTabEvent — dispatches messages arriving from other tabs.
  // ------------------------------------------------------------------
  const handleCrossTabEvent = (data) => {
    try {
      const msg = typeof data === 'string' ? JSON.parse(data) : data;
      if (!msg?.type) return;

      switch (msg.type) {
        case 'SESSION_LOGOUT':
          if (!isLoggingOutRef.current) logoutOnce('cross-tab');
          break;

        case 'SESSION_ACTIVITY':
          // Another tab had genuine user activity.
          // Use syncActivityFromExternal so we do NOT re-broadcast (no loop).
          if (typeof msg.lastActivity === 'number') {
            syncActivityFromExternal(msg.lastActivity);
          }
          break;

        case 'SESSION_EXTENDED':
          // Another tab explicitly extended the session.
          // Use syncActivityFromExternal so we do NOT re-broadcast (no loop).
          syncActivityFromExternal(msg.ts ?? Date.now());
          break;

        default:
          break;
      }
    } catch (_) {}
  };

  // ------------------------------------------------------------------
  // Effect: cross-tab sync setup — runs once on mount.
  // BroadcastChannel primary, localStorage storage-event as fallback.
  // ------------------------------------------------------------------
  useEffect(() => {
    let channel = null;

    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(CROSS_TAB_CHANNEL);
      channel.onmessage = (e) => handleCrossTabEvent(e.data);
      channelRef.current = channel;
    }

    const onStorage = (e) => {
      if (e.key === STORAGE_SYNC_KEY && e.newValue) {
        handleCrossTabEvent(e.newValue);
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('storage', onStorage);
      channel?.close();
      channelRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------------------------------------------
  // Effect: DOM activity listeners — only attached while a session is active.
  //
  // Passive listeners (scroll, touchstart, touchmove): browser can optimise
  //   scrolling without waiting for the handler to return.
  // Non-passive listeners (mousedown, mousemove, keydown, pointerdown):
  //   kept with default options so they remain cancellable if ever needed.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!user) return;   // Guard: no timers/listeners for unauthenticated state

    const PASSIVE_EVENTS  = ['scroll', 'touchstart', 'touchmove'];
    const ACTIVE_EVENTS   = ['mousedown', 'mousemove', 'keydown', 'pointerdown'];

    const handleActivity = () => resetActivity();

    PASSIVE_EVENTS.forEach((evt) =>
      window.addEventListener(evt, handleActivity, { passive: true })
    );
    ACTIVE_EVENTS.forEach((evt) =>
      window.addEventListener(evt, handleActivity)
    );

    return () => {
      PASSIVE_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, handleActivity, { passive: true })
      );
      ACTIVE_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, handleActivity)
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ------------------------------------------------------------------
  // Effect: visibility change — re-validate when tab regains focus.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!user) return;   // Guard: skip for unauthenticated state

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') revalidateOnFocus();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ------------------------------------------------------------------
  // Effect: wire axiosClient callbacks once on mount.
  // We wrap in lambdas so the interceptor always calls the most recent
  // closure version (avoids stale-closure issues with setters).
  // ------------------------------------------------------------------
  useEffect(() => {
    setActivityResetter(() => resetActivity());
    setSessionLogoutOnce(() => logoutOnce('session_expired'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------------------------------------------
  // Effect: start / stop session timers whenever `user` changes.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (user) {
      scheduleIdleTimer();
      startAbsoluteTimer();
    } else {
      stopAllTimers();
      setWarningVisible(false);
      isLoggingOutRef.current = false;   // reset for the next login
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ------------------------------------------------------------------
  // loadUser — restore session from localStorage on app mount.
  // /auth/me is marked skipActivityTracking so it never resets the idle
  // timer (background system call, not user activity).
  // ------------------------------------------------------------------
  const loadUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      localStorage.removeItem('user');
      clearAiStudioSessions();
      setUser(null);
      setLoading(false);
      return;
    }

    const storedUser = readStoredUser();
    if (storedUser) {
      setUser(storedUser);
      applyUserLanguage(storedUser);
    }

    try {
      const res = await axios.get('/auth/me', { skipActivityTracking: true });
      const userData = normalizeTenantUser(res.data.user, storedUser);
      setUser(userData);
      writeStoredUser(userData);
      applyUserLanguage(userData);
    } catch (err) {
      // 401 SESSION_EXPIRED is handled by the axios interceptor → logoutOnce.
      // Any other error: clear state so the user must log in again.
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      clearAiStudioSessions();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------------------------------------------
  // login — called by login pages after a successful POST /login.
  // ------------------------------------------------------------------
  const login = (userData, token) => {
    isLoggingOutRef.current = false;
    const normalizedUser = normalizeTenantUser(userData);
    localStorage.setItem('token', token);
    writeStoredUser(normalizedUser);
    setUser(normalizedUser);
    applyUserLanguage(normalizedUser);
    // Timers are started reactively by the `user` useEffect above.
  };

  // ------------------------------------------------------------------
  // logout — public API; delegates to the deduplicated logoutOnce.
  // ------------------------------------------------------------------
  const logout = () => logoutOnce('manual');

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}

      {/* Warning modal — rendered only when a session is active AND idle */}
      {warningVisible && user && (
        <SessionTimeoutModal
          secondsLeft={warningSecondsLeft}
          onExtend={extendSession}
          onLogout={() => logoutOnce('manual')}
        />
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
